import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="email-recipient"
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "selectedUsers",
    "recipientEmailsInput",
    "sendButton",
    "recipientCount",
    "subjectInput",
    "bodyInput",
  ];

  static values = {
    searchUrl: String,
    maxRecipients: { type: Number, default: 100 },
  };

  connect() {
    this.selectedUserEmails = [];
    this.searchTimeout = null;
    this.updateSendButton();
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  // Update the hidden input field with selected emails
  updateHiddenInput() {
    this.recipientEmailsInputTarget.value = this.selectedUserEmails.join(",");
  }

  // Handle search input with debouncing
  search(event) {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.performSearch(event.target.value);
    }, 300);
  }

  // Perform the actual search
  performSearch(query) {
    if (query.length < 2) {
      this.searchResultsTarget.classList.add("hidden");
      return;
    }

    fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.length > 0) {
          this.searchResultsTarget.innerHTML = data
            .map(
              (user) => `
            <div class="px-3 py-2 hover:bg-surface-alt cursor-pointer user-option"
                 data-user-id="${user.id}"
                 data-user-email="${user.email_address}"
                 data-user-name="${user.name}"
                 data-action="click->email-recipient#selectUser">
              <div class="font-medium text-on-surface">${user.name}</div>
              <div class="text-sm text-on-surface-muted">${user.email_address}</div>
            </div>
          `,
            )
            .join("");
          this.searchResultsTarget.classList.remove("hidden");
        } else {
          this.searchResultsTarget.innerHTML =
            '<div class="px-3 py-2 text-sm text-on-surface-muted">No users found</div>';
          this.searchResultsTarget.classList.remove("hidden");
        }
      })
      .catch((error) => {
        console.error("Error searching users:", error);
        this.searchResultsTarget.classList.add("hidden");
      });
  }

  // Handle user selection from search results
  selectUser(event) {
    const userOption = event.target.closest(".user-option");
    if (!userOption) return;

    const userId = userOption.dataset.userId;
    const userEmail = userOption.dataset.userEmail;
    const userName = userOption.dataset.userName;

    if (!this.selectedUserEmails.includes(userEmail)) {
      if (this.selectedUserEmails.length >= this.maxRecipientsValue) {
        alert(
          `You can only select up to ${this.maxRecipientsValue} recipients.`,
        );
        return;
      }

      this.selectedUserEmails.push(userEmail);
      this.addSelectedUser(userId, userEmail, userName);
      this.updateHiddenInput();
      this.updateSendButton();
    }

    this.searchInputTarget.value = "";
    this.searchResultsTarget.classList.add("hidden");
  }

  // Add selected user to the display list
  addSelectedUser(userId, userEmail, userName) {
    const userDiv = document.createElement("div");
    userDiv.className =
      "flex items-center justify-between bg-surface-alt rounded-radius p-2";
    userDiv.innerHTML = `
      <div>
        <div class="font-medium text-on-surface">${userName}</div>
        <div class="text-sm text-on-surface-muted">${userEmail}</div>
      </div>
      <button type="button"
              class="text-danger hover:text-danger-dark remove-user"
              data-email="${userEmail}"
              data-action="click->email-recipient#removeUser">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;
    this.selectedUsersTarget.appendChild(userDiv);
  }

  // Remove selected user
  removeUser(event) {
    const button = event.target.closest(".remove-user");
    if (!button) return;

    const email = button.dataset.email;
    this.selectedUserEmails = this.selectedUserEmails.filter(
      (em) => em !== email,
    );
    button.closest(".bg-surface-alt").remove();
    this.updateHiddenInput();
    this.updateSendButton();
  }

  // Update send button state
  updateSendButton() {
    const hasRecipients = this.selectedUserEmails.length > 0;
    const hasSubject = this.subjectInputTarget.value.trim() !== "";
    const hasBody = this.bodyInputTarget.value.trim() !== "";

    this.sendButtonTarget.disabled = !(hasRecipients && hasSubject && hasBody);

    // Update recipient count display
    if (this.hasRecipientCountTarget) {
      if (this.selectedUserEmails.length === 0) {
        this.recipientCountTarget.textContent = "";
      } else {
        const count = this.selectedUserEmails.length;
        this.recipientCountTarget.textContent = `(${count} recipient${count > 1 ? "s" : ""} selected)`;
      }
    }
  }

  // Handle form field changes
  updateForm() {
    this.updateSendButton();
  }

  // Close search results when clicking outside
  closeSearchResults(event) {
    if (
      !this.searchInputTarget.contains(event.target) &&
      !this.searchResultsTarget.contains(event.target)
    ) {
      this.searchResultsTarget.classList.add("hidden");
    }
  }

  // Validate form before submission
  validateForm(event) {
    if (this.selectedUserEmails.length === 0) {
      event.preventDefault();
      alert("Please select at least one recipient before sending the email.");
      return false;
    }
  }
}
